// src/views/admin/ADBulletins.jsx
import { useState, useEffect, useMemo } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import BulletinCard from "./BulletinCard";
import "./bulletins.css";

/* ============================================================================
 * CONSTANTES
 * ========================================================================== */

const STATUT_LABELS = {
  envoye: { label: "Envoyé", cls: "ok" },
  non_envoye: { label: "Non envoyé", cls: "neu" },
  erreur: { label: "Erreur", cls: "rd" },
};

const MODALS = {
  CONFIRM_PUBLIER: "confirm-publier",
  CONFIRM_RESET: "confirm-reset",
  ENVOI_RESULTAT: "envoi-resultat",
};

/* ============================================================================
 * HELPERS
 * ========================================================================== */

// le regroupement par classe soit cohérent entre saisie des notes et bulletins)
function getEleveClasse(eleve, anneeId) {
  if (eleve.classe_id !== undefined) {
    return { classeId: eleve.classe_id, classeNom: eleve.classe_nom || "Non assigné" };
  }
  const insc = (eleve.inscriptions || []).find((i) => String(i.annee_scolaire) === String(anneeId));
  return {
    classeId: insc?.classe ?? null,
    classeNom: insc?.classe_nom ?? "Non assigné",
  };
}

// caractères spéciaux) sans dépendance externe.
function slugifyFilename(str) {
  return (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

/* ============================================================================
 * HOOK — état + logique métier
 * ========================================================================== */

function useBulletinsAdmin(slug) {
  const toast = useToastHook();
  const {
    getAnneesScolaires,
    getTrimestres,
    getPlannifications,
    getEleves,
    genererBulletinsParPlannification,
    genererBulletinsParEleves,
    getStatutEnvoiBulletins,
    reinitialiserStatutEnvoi,
    publierBulletins,
    envoyerBulletinsParentsEmail,
    envoyerBulletinIndividuelEmail,
    envoyerBulletinsParentsWhatsapp,
    envoyerBulletinIndividuelWhatsapp,
    getBulletinDetail,
    telechargerBulletinsPDF,
  } = useAdminService();

  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [trimestres, setTrimestres] = useState([]);
  const [trimestreFilt, setTrimestreFilt] = useState("");
  const [plannifications, setPlannifications] = useState([]);
  const [plannifFilt, setPlannifFilt] = useState("");

  const [allEleves, setAllEleves] = useState(null);
  const [loadingEleves, setLoadingEleves] = useState(false);

  // automatique s'il n'y a qu'une classe, sinon écran de choix plein écran.
  const [classeFilt, setClasseFilt] = useState("");
  const [showClassSelector, setShowClassSelector] = useState(false);

  const [statut, setStatut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatut, setLoadingStatut] = useState(false);
  const [working, setWorking] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [previewFiltreEleveIds, setPreviewFiltreEleveIds] = useState(null);

  const [modal, setModal] = useState(null);
  const [envoiResultat, setEnvoiResultat] = useState(null);

  const [bulletinsDetails, setBulletinsDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    if (slug) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadTrimestresEtPlannifs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, anneeFilt]);

  useEffect(() => {
    setPlannifFilt("");
    setStatut(null);
    setSelectedIds([]);
    setPreviewFiltreEleveIds(null);
  }, [trimestreFilt]);

  useEffect(() => {
    if (slug && plannifFilt) {
      ensureElevesLoaded();
      loadStatut();
    } else {
      setStatut(null);
    }
    setSelectedIds([]);
    setSearch("");
    setPreviewFiltreEleveIds(null);
    setClasseFilt("");
    setShowClassSelector(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, plannifFilt]);

  useEffect(() => {
    setSelectedIds([]);
    setSearch("");
    setPreviewFiltreEleveIds(null);
  }, [classeFilt]);

  useEffect(() => {
    const bulletins = filtrerBulletinsPourApercu(statut?.bulletins, previewFiltreEleveIds);
    const ids = bulletins.map((b) => b.id);
    if (ids.length === 0) {
      setBulletinsDetails({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDetails(true);
      try {
        const entries = await Promise.all(
          ids.map(async (id) => {
            try {
              const data = await getBulletinDetail(slug, id);
              return [id, data];
            } catch {
              return [id, null];
            }
          })
        );
        if (!cancelled) setBulletinsDetails(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statut, previewFiltreEleveIds]);

  const init = async () => {
    setLoading(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find((a) => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
    }
  };

  const loadTrimestresEtPlannifs = async () => {
    try {
      const [trimData, plannifData] = await Promise.all([
        getTrimestres(slug, { annee_scolaire: anneeFilt }),
        getPlannifications(slug, { annee_scolaire: anneeFilt }),
      ]);
      setTrimestres(trimData);
      setPlannifications(plannifData);
    } catch (error) {
      toast.error("Impossible de charger les trimestres/plannifications");
    }
  };

  const ensureElevesLoaded = async () => {
    if (allEleves !== null) return;
    setLoadingEleves(true);
    try {
      const data = await getEleves(slug, { page_size: "all" });
      setAllEleves(data.results || data || []);
    } catch (error) {
      toast.error("Impossible de charger la liste des élèves");
      setAllEleves([]);
    } finally {
      setLoadingEleves(false);
    }
  };

  const loadStatut = async () => {
    setLoadingStatut(true);
    try {
      const data = await getStatutEnvoiBulletins(slug, plannifFilt);
      setStatut(data);
    } catch (error) {
      toast.error("Impossible de charger le statut des bulletins");
      setStatut(null);
    } finally {
      setLoadingStatut(false);
    }
  };

  const plannifsFiltrees = plannifications.filter(
    (p) => !trimestreFilt || String(p.trimestre) === String(trimestreFilt)
  );

  const plannificationSelectionnee = plannifications.find(
    (p) => String(p.id) === String(plannifFilt)
  );

  const idsElevesConcernes = useMemo(
    () => new Set((plannificationSelectionnee?.eleves_concernes || []).map(String)),
    [plannificationSelectionnee]
  );

  const bulletinsParEleve = useMemo(() => {
    const map = {};
    for (const b of statut?.bulletins || []) {
      const eleveId = b.eleve ?? b.eleve_id;
      if (eleveId != null) map[String(eleveId)] = b;
    }
    return map;
  }, [statut]);

  const elevesConcernes = useMemo(() => {
    return (allEleves || [])
      .filter((e) => idsElevesConcernes.has(String(e.id)))
      .map((e) => ({ ...e, bulletin: bulletinsParEleve[String(e.id)] || null }));
  }, [allEleves, idsElevesConcernes, bulletinsParEleve]);

  // ✅ Classes disponibles parmi les élèves concernés par la plannification,
  // avec le nombre d'élèves par classe (pour l'écran de sélection).
  const classesDisponibles = useMemo(() => {
    const map = new Map();
    elevesConcernes.forEach((e) => {
      const { classeId, classeNom } = getEleveClasse(e, anneeFilt);
      if (classeId == null) return;
      if (!map.has(classeId)) map.set(classeId, { id: classeId, nom: classeNom, nbEleves: 0 });
      map.get(classeId).nbEleves += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [elevesConcernes, anneeFilt]);

  useEffect(() => {
    if (classesDisponibles.length === 1) {
      setClasseFilt(String(classesDisponibles[0].id));
      setShowClassSelector(false);
    } else if (classesDisponibles.length > 1 && !classeFilt) {
      setShowClassSelector(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classesDisponibles]);

  const handleClassSelection = (classeId) => {
    setClasseFilt(String(classeId));
    setShowClassSelector(false);
  };

  const handleChangeClasse = () => {
    setShowClassSelector(true);
    setClasseFilt("");
  };

  const elevesDeClasse = useMemo(() => {
    if (!classeFilt) return elevesConcernes;
    return elevesConcernes.filter((e) => {
      const { classeId } = getEleveClasse(e, anneeFilt);
      return String(classeId) === String(classeFilt);
    });
  }, [elevesConcernes, classeFilt, anneeFilt]);

  const elevesFiltres = elevesDeClasse.filter((e) => {
    const q = search.toLowerCase();
    return (
      !q ||
      `${e.prenom} ${e.nom}`.toLowerCase().includes(q) ||
      e.matricule?.toLowerCase().includes(q)
    );
  });

  // Calcul du nombre de bulletins dans l'aperçu (filtré ou total) — reste
  // à l'échelle de toute la plannification, cohérent avec les actions
  // globales (Publier / Envoyer tous) qui agissent sur toute la plannification.
  const bulletinsApercuCount = useMemo(() => {
    return filtrerBulletinsPourApercu(statut?.bulletins, previewFiltreEleveIds).length;
  }, [statut, previewFiltreEleveIds]);

  const bulletinsGeneres = useMemo(
    () => filtrerBulletinsPourApercu(statut?.bulletins, previewFiltreEleveIds),
    [statut, previewFiltreEleveIds]
  );

  // affichée — utilisé pour l'aperçu imprimable et le téléchargement PDF,
  // pour que "voir/télécharger" corresponde à ce qui est affiché à l'écran.
  const bulletinsPreview = useMemo(() => {
    if (!classeFilt) return bulletinsGeneres;
    const idsClasse = new Set(elevesDeClasse.map((e) => String(e.id)));
    return bulletinsGeneres.filter((b) => idsClasse.has(String(b.eleve ?? b.eleve_id)));
  }, [bulletinsGeneres, classeFilt, elevesDeClasse]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAllVisible = () =>
    setSelectedIds((prev) =>
      prev.length === elevesFiltres.length ? [] : elevesFiltres.map((e) => e.id)
    );

  const handleGenererTous = async () => {
    setWorking(true);
    try {
      const created = await genererBulletinsParPlannification(slug, plannifFilt);
      toast.success(`${created.length} bulletin(s) généré(s)/mis à jour`);
      setPreviewFiltreEleveIds(null);
      await loadStatut();
    } catch (error) {
      toast.error("Impossible de générer les bulletins (vérifie qu'une configuration de bulletin est active)");
    } finally {
      setWorking(false);
    }
  };

  const handleGenererSelection = async () => {
    if (selectedIds.length === 0) {
      toast.error("Sélectionne au moins un élève dans le tableau");
      return;
    }
    setWorking(true);
    try {
      const created = await genererBulletinsParEleves(slug, selectedIds, plannifFilt);
      toast.success(`${created.length} bulletin(s) généré(s)/mis à jour`);
      setPreviewFiltreEleveIds([...selectedIds]);
      await loadStatut();
    } catch (error) {
      toast.error("Impossible de générer les bulletins pour cette sélection");
    } finally {
      setWorking(false);
    }
  };

  const handlePublier = async () => {
    setWorking(true);
    try {
      const res = await publierBulletins(slug, plannifFilt);
      toast.success(res.message || "Bulletins publiés avec succès");
      setModal(null);
      await loadStatut();
    } catch (error) {
      toast.error("Impossible de publier les bulletins");
    } finally {
      setWorking(false);
    }
  };

  const handleReinitialiser = async () => {
    setWorking(true);
    try {
      const res = await reinitialiserStatutEnvoi(slug, plannifFilt);
      toast.success(res.message || "Statut d'envoi réinitialisé");
      setModal(null);
      setPreviewFiltreEleveIds(null);
      await loadStatut();
    } catch (error) {
      toast.error("Impossible de réinitialiser le statut d'envoi");
    } finally {
      setWorking(false);
    }
  };

  const bulletinIdsPourSelection = () => {
    const ids = selectedIds
      .map((eleveId) => bulletinsParEleve[String(eleveId)]?.id)
      .filter(Boolean);
    return ids;
  };

  const handleEnvoyerEmailSelection = async () => {
    const bulletinIds = bulletinIdsPourSelection();
    if (bulletinIds.length === 0) {
      toast.error("Aucun bulletin généré parmi la sélection — génère-les d'abord");
      return;
    }
    setWorking(true);
    try {
      const res = await envoyerBulletinsParentsEmail(slug, { bulletins: bulletinIds });
      setEnvoiResultat(res.resultats);
      setModal(MODALS.ENVOI_RESULTAT);
      await loadStatut();
    } catch (error) {
      toast.error("Erreur lors de l'envoi par email");
    } finally {
      setWorking(false);
    }
  };

  const handleEnvoyerWhatsappSelection = async () => {
    const bulletinIds = bulletinIdsPourSelection();
    if (bulletinIds.length === 0) {
      toast.error("Aucun bulletin généré parmi la sélection — génère-les d'abord");
      return;
    }
    setWorking(true);
    try {
      const res = await envoyerBulletinsParentsWhatsapp(slug, { bulletins: bulletinIds });
      setEnvoiResultat(res.resultats);
      setModal(MODALS.ENVOI_RESULTAT);
      await loadStatut();
    } catch (error) {
      toast.error("Erreur lors de l'envoi par WhatsApp");
    } finally {
      setWorking(false);
    }
  };

  const handleEnvoyerEmailTous = async () => {
    setWorking(true);
    try {
      const res = await envoyerBulletinsParentsEmail(slug, { plannification_id: plannifFilt });
      setEnvoiResultat(res.resultats);
      setModal(MODALS.ENVOI_RESULTAT);
      await loadStatut();
    } catch (error) {
      toast.error("Erreur lors de l'envoi par email");
    } finally {
      setWorking(false);
    }
  };

  const handleEnvoyerWhatsappTous = async () => {
    setWorking(true);
    try {
      const res = await envoyerBulletinsParentsWhatsapp(slug, { plannification_id: plannifFilt });
      setEnvoiResultat(res.resultats);
      setModal(MODALS.ENVOI_RESULTAT);
      await loadStatut();
    } catch (error) {
      toast.error("Erreur lors de l'envoi par WhatsApp");
    } finally {
      setWorking(false);
    }
  };

  const handleEnvoyerIndividuelEmail = async (bulletinId) => {
    setWorking(true);
    try {
      const res = await envoyerBulletinIndividuelEmail(slug, bulletinId);
      toast.success(res.message || "Bulletin envoyé");
      await loadStatut();
    } catch (error) {
      toast.error("Impossible d'envoyer ce bulletin par email");
    } finally {
      setWorking(false);
    }
  };

  const handleEnvoyerIndividuelWhatsapp = async (bulletinId) => {
    setWorking(true);
    try {
      const res = await envoyerBulletinIndividuelWhatsapp(slug, bulletinId);
      toast.success(res.message || "Bulletin envoyé");
      await loadStatut();
    } catch (error) {
      toast.error("Impossible d'envoyer ce bulletin par WhatsApp");
    } finally {
      setWorking(false);
    }
  };

  // ─── PDF / Impression (génération serveur via WeasyPrint) ──────────────
  // Payload : si un filtre de sélection ou de classe est actif, on envoie
  // la liste précise des bulletins (bulletinsPreview) ; sinon on envoie
  // toute la plannification.
  const payloadPourImpression = () => {
    const ids = bulletinsPreview.map((b) => b.id);
    if (previewFiltreEleveIds || classeFilt) {
      return { bulletins: ids };
    }
    return { plannification_id: plannifFilt };
  };

  // l'aperçu, sinon nom de la classe si une classe est sélectionnée,
  // sinon juste le nom de la plannification (toutes classes confondues).
  const nomFichierPourImpression = () => {
    const plannifNom = plannifsFiltrees.find((p) => String(p.id) === String(plannifFilt))?.nom || "bulletins";
    const dateGen = new Date().toLocaleDateString("fr-FR").replace(/\//g, "-");
    let detail = "";
    if (bulletinsPreview.length === 1) {
      const bulletin = bulletinsPreview[0];
      const eleve = elevesConcernes.find(
        (e) => String(e.id) === String(bulletin.eleve ?? bulletin.eleve_id)
      );
      if (eleve) detail = `_${eleve.nom}_${eleve.prenom}`;
    } else if (classeFilt) {
      const classeNom = classesDisponibles.find((c) => String(c.id) === String(classeFilt))?.nom;
      if (classeNom) detail = `_${classeNom}`;
    }
    return slugifyFilename(`bulletins_${plannifNom}${detail}_${dateGen}`);
  };

  const handleDownloadPDF = async () => {
    if (bulletinsPreview.length === 0) {
      toast.error("Aucun bulletin à exporter");
      return;
    }
    setGeneratingPDF(true);
    try {
      const blob = await telechargerBulletinsPDF(slug, payloadPourImpression());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nomFichierPourImpression()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré avec succès");
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleImprimer = async () => {
    if (bulletinsPreview.length === 0) {
      toast.error("Aucun bulletin à imprimer");
      return;
    }
    setGeneratingPDF(true);
    try {
      const blob = await telechargerBulletinsPDF(slug, payloadPourImpression());
      const url = window.URL.createObjectURL(blob);
      // Ouvre le PDF dans un nouvel onglet : le viewer natif du navigateur
      // gère l'aperçu et l'impression (Ctrl+P), plus fidèle que html2canvas.
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Impossible d'imprimer les bulletins");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const closeModal = () => setModal(null);

  return {
    annees, anneeFilt, setAnneeFilt,
    trimestres, trimestreFilt, setTrimestreFilt,
    plannifsFiltrees, plannifFilt, setPlannifFilt,
    classeFilt, classesDisponibles, showClassSelector,
    handleClassSelection, handleChangeClasse,
    statut, loading, loadingStatut, loadingEleves, working, loadStatut,
    search, setSearch, elevesFiltres, selectedIds,
    toggleSelect, selectAllVisible,
    modal, setModal, closeModal, envoiResultat,
    bulletinsDetails, loadingDetails,
    previewFiltreEleveIds, bulletinsApercuCount, bulletinsGeneres, bulletinsPreview,
    generatingPDF, handleDownloadPDF, handleImprimer,
    handleGenererTous, handleGenererSelection, handlePublier, handleReinitialiser,
    handleEnvoyerEmailSelection, handleEnvoyerWhatsappSelection,
    handleEnvoyerEmailTous, handleEnvoyerWhatsappTous,
    handleEnvoyerIndividuelEmail, handleEnvoyerIndividuelWhatsapp,
  };
}

/* ============================================================================
 * UI — briques d'affichage
 * ========================================================================== */

function Modal({ title, onClose, children, maxWidth = 450 }) {
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── 1. Sélecteur de plannification ──────────────────────────────────── */

function PlannificationSelector({
  annees, anneeFilt, onAnneeChange,
  trimestres, trimestreFilt, onTrimestreChange,
  plannifsFiltrees, plannifFilt, onPlannifChange,
  onRefresh,
}) {
  return (
    <div className="toolbar">
      <div className="sb2" style={{ flexWrap: "wrap" }}>
        <select value={anneeFilt} onChange={(e) => onAnneeChange(e.target.value)}>
          {annees.map((a) => (
            <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>
          ))}
        </select>
        <select value={trimestreFilt} onChange={(e) => onTrimestreChange(e.target.value)}>
          <option value="">Tous les trimestres</option>
          {trimestres.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
        </select>
        <select value={plannifFilt} onChange={(e) => onPlannifChange(e.target.value)}>
          <option value="">Sélectionner une plannification</option>
          {plannifsFiltrees.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        <button className="bsec" onClick={onRefresh} disabled={!plannifFilt}>
          <i className="ti ti-refresh" />Actualiser
        </button>
      </div>
    </div>
  );
}

/* ── 1bis. Sélecteur de classe plein écran ───────────────────────────────
   Même principe que ADNotes.jsx : quand la plannification concerne des
   élèves de plusieurs classes, on demande d'en choisir une avant d'afficher
   la liste et l'aperçu des bulletins. */

function ClasseSelector({ classes, onSelect }) {
  return (
    <div className="card" style={{ textAlign: "center", padding: 32 }}>
      <h3 style={{ color: "var(--pr, #004080)", marginBottom: 10 }}>
        <i className="ti ti-users" /> Sélectionnez une classe
      </h3>
      <p style={{ marginBottom: 24, color: "var(--ink-soft)", fontSize: 15 }}>
        Cette plannification concerne des élèves de plusieurs classes. Choisis une classe
        pour gérer ses bulletins.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        {classes.map((classe) => (
          <button
            key={classe.id}
            onClick={() => onSelect(classe.id)}
            style={{
              background: "var(--bg, #f8f9fa)",
              border: "2px solid var(--pr, #004080)",
              borderRadius: 10,
              padding: 20,
              cursor: "pointer",
              textAlign: "center",
              color: "inherit",
            }}
          >
            <i className="ti ti-chalkboard" style={{ fontSize: 32, marginBottom: 8, display: "block" }} />
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{classe.nom}</div>
            <div style={{ fontSize: 13 }}>{classe.nbEleves} élève(s)</div>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 20 }}>
        Vous pourrez changer de classe à tout moment.
      </div>
    </div>
  );
}

/* ── 2. Recherche + compteur de sélection ────────────────────────────── */

function SelectionBar({ search, onSearchChange, selectedCount, total }) {
  return (
    <div className="toolbar">
      <div className="selection-bar">
        <div className="sb2">
          <i className="ti ti-search" style={{ color: "var(--ink-soft)" }} />
          <input
            type="text" placeholder="Rechercher nom, prénom, matricule…"
            value={search} onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="sb2">
          <span className="selection-count">
            <i className="ti ti-user-check" />{selectedCount} / {total} sélectionné(s)
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── 3. Liste des élèves en tableau ─────────────────────────────────── */

function ElevesList({
  eleves, selectedIds, onToggleSelect, working,
  onEnvoyerEmail, onEnvoyerWhatsapp,
}) {
  if (eleves.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <strong>Aucun élève trouvé</strong>
          Aucun élève n'est concerné par cette plannification.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: "6%" }}></th>
            <th style={{ width: "18%" }}>Matricule</th>
            <th style={{ width: "24%" }}>Nom complet</th>
            <th style={{ width: "14%" }}>Statut</th>
            <th style={{ width: "13%" }}>Moyenne</th>
            <th style={{ width: "9%" }}>Rang</th>
            <th style={{ width: "16%" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {eleves.map((e) => {
            const b = e.bulletin;
            const genere = Boolean(b);
            const st = genere ? (STATUT_LABELS[b.statut_envoi] || STATUT_LABELS.non_envoye) : null;
            const isSelected = selectedIds.includes(e.id);
            return (
              <tr
                key={e.id}
                className={genere ? "is-generee" : ""}
                onClick={() => onToggleSelect(e.id)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ textAlign: "center" }} onClick={(ev) => ev.stopPropagation()}>
                  <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(e.id)} />
                </td>
                <td><span className="matricule">{e.matricule}</span></td>
                <td style={{ fontWeight: 500 }}>{e.prenom} {e.nom}</td>
                <td>
                  {genere ? (
                    <span className={`pill ${st.cls}`}>{st.label}</span>
                  ) : (
                    <span className="pill neu">Non généré</span>
                  )}
                </td>
                <td>{genere && b.moyenne_generale != null ? b.moyenne_generale.toFixed(2) : "—"}</td>
                <td>{genere ? (b.rang ?? "—") : "—"}</td>
                <td onClick={(ev) => ev.stopPropagation()}>
                  <div className="act">
                    <button
                      title="Envoyer par email"
                      disabled={!genere || working || !b?.parent_email}
                      onClick={() => onEnvoyerEmail(b.id)}
                    >
                      <i className="ti ti-mail" />
                    </button>
                    <button
                      title="Envoyer par WhatsApp"
                      disabled={!genere || working || !b?.parent_telephone}
                      onClick={() => onEnvoyerWhatsapp(b.id)}
                    >
                      <i className="ti ti-brand-whatsapp" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── 4. Aperçu imprimable ───────────────────────────────────────────── */

function BulletinsPreviewArea({
  bulletins, details, loading,
  onDownloadPDF, onImprimer, generatingPDF,
}) {
  if (bulletins.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="preview-area-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="ti ti-file-text" />
          <h3>Bulletins générés ({bulletins.length})</h3>
          {loading && <span className="preview-area-loading"><i className="ti ti-loader-2" />Chargement des aperçus…</span>}
        </div>
        <div className="export-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            className="bsec"
            onClick={onDownloadPDF}
            disabled={generatingPDF || loading}
            style={{ color: "#dc3545", borderColor: "#dc3545" }}
          >
            <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
          </button>
          <button
            className="bsec"
            onClick={onImprimer}
            disabled={generatingPDF || loading}
            style={{ color: "#6f42c1", borderColor: "#6f42c1" }}
          >
            <i className="ti ti-printer" /> {generatingPDF ? "Génération..." : "Imprimer"}
          </button>
        </div>
      </div>

      <div className="bulletin-preview-area">
        {bulletins.map((b) => {
          const data = details[b.id];
          return (
            <div key={b.id} className="bulletin-page-wrap" data-bulletin-id={b.id}>
              {data ? (
                <BulletinCard bulletin={data} />
              ) : (
                <div className="empty-state" style={{ minHeight: 200 }}>
                  {loading ? "Chargement…" : "Aperçu indisponible pour ce bulletin"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── 5. Barre d'actions globale, fixée en bas ────────────────────────── */

function StickyActionBar({
  total, working, multiClasses,
  onPublier, onEnvoyerEmailTous, onEnvoyerWhatsappTous, onReinitialiser,
}) {
  if (!total) return null;
  return (
    <div className="sticky-actionbar">
      <button className="bprim" onClick={onPublier} disabled={working}>
        <i className="ti ti-send" />{working ? "Publication en cours…" : `Publier ${total} bulletin(s)`}
      </button>
      <button className="bsec" onClick={onEnvoyerEmailTous} disabled={working}>
        <i className="ti ti-mail" />Envoyer {total} bulletin(s) par email
      </button>
      <button className="bwhatsapp" onClick={onEnvoyerWhatsappTous} disabled={working}>
        <i className="ti ti-brand-whatsapp" />Envoyer {total} bulletin(s) par WhatsApp
      </button>
      <button className="bsec rd" onClick={onReinitialiser} disabled={working}>
        <i className="ti ti-rotate" />Réinitialiser envoi
      </button>
      <div className="sticky-actionbar-note">
        {total} bulletin(s) généré(s) — prêts à publier ou envoyer
        {multiClasses && " (toutes classes de cette plannification confondues)"}
      </div>
    </div>
  );
}

/* ── Modales ──────────────────────────────────────────────────────────── */

function ConfirmPublierModal({ total, working, onClose, onConfirm }) {
  return (
    <Modal title="Publier les bulletins" onClose={onClose}>
      <div style={{ fontSize: 14, color: "var(--ink-soft)" }}>
        Rendre les {total} bulletin(s) de cette plannification visibles par les parents
        (portail + notification push) ?
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button className="bprim" onClick={onConfirm} disabled={working}>
          {working ? "..." : <><i className="ti ti-send" />Publier</>}
        </button>
      </div>
    </Modal>
  );
}

function ConfirmResetModal({ working, onClose, onConfirm }) {
  return (
    <Modal title="Réinitialiser le statut d'envoi" onClose={onClose}>
      <div style={{ fontSize: 14, color: "var(--ink-soft)" }}>
        Remettre à "non envoyé" le statut de tous les bulletins de cette plannification ?
        <div style={{ color: "var(--danger)", marginTop: 8 }}>
          ⚠️ N'annule pas les envois déjà effectués, seulement leur statut affiché.
        </div>
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button className="bprim rd" onClick={onConfirm} disabled={working}>
          {working ? "..." : <><i className="ti ti-rotate" />Réinitialiser</>}
        </button>
      </div>
    </Modal>
  );
}

function EnvoiResultatModal({ resultat, onClose }) {
  if (!resultat) return null;
  const succes = resultat.emails_envoyes ?? resultat.messages_envoyes ?? 0;
  const echecs = resultat.emails_echec ?? resultat.messages_echec ?? 0;

  return (
    <Modal title="Résultat de l'envoi" onClose={onClose} maxWidth={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="sb2">
          <span className="pill ok">Succès : {succes}</span>
          <span className="pill rd">Échecs : {echecs}</span>
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          {(resultat.details || []).map((d, i) => (
            <div key={i} style={{ fontSize: 13, borderBottom: "1px solid var(--line)", paddingBottom: 6 }}>
              <span className={`pill ${d.statut === "succes" ? "ok" : "rd"}`} style={{ marginRight: 6 }}>
                {d.statut === "succes" ? "OK" : "Échec"}
              </span>
              {d.email || d.numero || d.eleve}
              {d.raison && <div style={{ color: "var(--danger)" }}>{d.raison}</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="factions">
        <button className="bprim" onClick={onClose}>Fermer</button>
      </div>
    </Modal>
  );
}

/* ============================================================================
 * COMPOSANT PRINCIPAL
 * ========================================================================== */

export default function ADBulletins({ slug }) {
  const s = useBulletinsAdmin(slug);

  if (s.loading) return <Loader />;

  const toutSelectionne = s.selectedIds.length === s.elevesFiltres.length && s.elevesFiltres.length > 0;
  const multiClasses = s.classesDisponibles.length > 1;

  return (
    <div>

      {/* 2. Sélecteur de plannification ---------------------------------- */}
      <PlannificationSelector
        annees={s.annees} anneeFilt={s.anneeFilt} onAnneeChange={s.setAnneeFilt}
        trimestres={s.trimestres} trimestreFilt={s.trimestreFilt} onTrimestreChange={s.setTrimestreFilt}
        plannifsFiltrees={s.plannifsFiltrees} plannifFilt={s.plannifFilt} onPlannifChange={s.setPlannifFilt}
        onRefresh={s.loadStatut}
      />

      {!s.plannifFilt && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="empty-state">
            <strong>Aucune plannification sélectionnée</strong>
            Choisis une plannification pour commencer la gestion des bulletins.
          </div>
        </div>
      )}

      {s.plannifFilt && (
        s.loadingStatut || s.loadingEleves ? <Loader /> :
        s.showClassSelector && multiClasses ? (
          /* 2bis. Écran de choix de classe (plannification multi-classes) */
          <ClasseSelector classes={s.classesDisponibles} onSelect={s.handleClassSelection} />
        ) : (
          <>
            {/* Bandeau "classe actuelle" + changer de classe, si applicable */}
            {multiClasses && s.classeFilt && (
              <div className="toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="ti ti-chalkboard" />
                  Classe : <strong>{s.classesDisponibles.find((c) => String(c.id) === String(s.classeFilt))?.nom}</strong>
                </span>
                <button className="bsec" onClick={s.handleChangeClasse}>
                  <i className="ti ti-users" /> Changer de classe
                </button>
              </div>
            )}

            {/* 3. Recherche + actions de sélection ------------------------ */}
            <SelectionBar
              search={s.search} onSearchChange={s.setSearch}
              selectedCount={s.selectedIds.length} total={s.elevesFiltres.length}
            />

            {s.elevesFiltres.length > 0 && (
              <div className="sb2" style={{ marginTop: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <button className="bprim" onClick={s.handleGenererTous} disabled={s.working}>
                  <i className="ti ti-refresh" />
                  {s.working ? "Génération en cours…" : "Générer/régénérer pour toute la plannification"}
                </button>
                <button className="bsec" onClick={s.selectAllVisible}>
                  <i className={`ti ti-${toutSelectionne ? "square" : "checkbox"}`} />
                  {toutSelectionne ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
                {s.selectedIds.length > 0 && (
                  <>
                    <button className="bprim" onClick={s.handleGenererSelection} disabled={s.working}>
                      <i className="ti ti-refresh" />Générer ({s.selectedIds.length})
                    </button>
                    <button className="bsec" onClick={s.handleEnvoyerEmailSelection} disabled={s.working}>
                      <i className="ti ti-mail" />Envoyer email ({s.selectedIds.length})
                    </button>
                    <button className="bwhatsapp" onClick={s.handleEnvoyerWhatsappSelection} disabled={s.working}>
                      <i className="ti ti-brand-whatsapp" />Envoyer WhatsApp ({s.selectedIds.length})
                    </button>
                  </>
                )}
              </div>
            )}

            {/* 4. Liste des élèves (tableau) ----------------------------- */}
            <ElevesList
              eleves={s.elevesFiltres}
              selectedIds={s.selectedIds}
              onToggleSelect={s.toggleSelect}
              working={s.working}
              onEnvoyerEmail={s.handleEnvoyerIndividuelEmail}
              onEnvoyerWhatsapp={s.handleEnvoyerIndividuelWhatsapp}
            />

            {/* 5. Aperçu imprimable des bulletins générés -----------------
                Limité à la classe actuellement affichée : le PDF téléchargé
                ou imprimé correspond à ce qui est visible à l'écran. */}
            <BulletinsPreviewArea
              bulletins={s.bulletinsPreview}
              details={s.bulletinsDetails}
              loading={s.loadingDetails}
              onDownloadPDF={s.handleDownloadPDF}
              onImprimer={s.handleImprimer}
              generatingPDF={s.generatingPDF}
            />
          </>
        )
      )}

      {/* 6. Barre d'actions globale, fixée en bas ------------------------ */}
      {/* Utilise bulletinsApercuCount (toute la plannification) pour rester
          cohérent avec les actions "Publier/Envoyer tous", qui agissent sur
          la plannification entière quelle que soit la classe affichée. */}
      {s.plannifFilt && !s.loadingStatut && !(s.showClassSelector && multiClasses) && (
        <StickyActionBar
          total={s.bulletinsApercuCount}
          working={s.working}
          multiClasses={multiClasses}
          onPublier={() => s.setModal(MODALS.CONFIRM_PUBLIER)}
          onEnvoyerEmailTous={s.handleEnvoyerEmailTous}
          onEnvoyerWhatsappTous={s.handleEnvoyerWhatsappTous}
          onReinitialiser={() => s.setModal(MODALS.CONFIRM_RESET)}
        />
      )}

      {/* Modales ----------------------------------------------------------- */}
      {s.modal === MODALS.CONFIRM_PUBLIER && (
        <ConfirmPublierModal
          total={s.bulletinsApercuCount}
          working={s.working}
          onClose={s.closeModal}
          onConfirm={s.handlePublier}
        />
      )}

      {s.modal === MODALS.CONFIRM_RESET && (
        <ConfirmResetModal working={s.working} onClose={s.closeModal} onConfirm={s.handleReinitialiser} />
      )}

      {s.modal === MODALS.ENVOI_RESULTAT && (
        <EnvoiResultatModal resultat={s.envoiResultat} onClose={s.closeModal} />
      )}
    </div>
  );
}

// Filtre les bulletins renvoyés par getStatutEnvoiBulletins pour n'afficher,
// dans la zone d'aperçu imprimable, que ceux des élèves concernés par la
// dernière génération (eleveIds === null => aucun filtre, on montre tout).
function filtrerBulletinsPourApercu(bulletins, eleveIds) {
  const all = bulletins || [];
  if (eleveIds === null || eleveIds === undefined) return all;
  const idsSet = new Set(eleveIds.map(String));
  return all.filter((b) => idsSet.has(String(b.eleve ?? b.eleve_id)));
}